import { call, put, takeLatest } from 'redux-saga/effects';
import api from '../../helpers/api';
import {
  FETCH_TEAMS, FETCH_TEAM,
  CREATE_TEAM, UPDATE_TEAM, DELETE_TEAM,
  ADD_TEAM_MEMBER, REMOVE_TEAM_MEMBER, UPDATE_TEAM_MEMBER_ROLE,
  FETCH_TEAM_RESPONSIBILITIES, ATTACH_TEAM_RESPONSIBILITY,
  ATTACH_TEAM_RESPONSIBILITIES_BULK, DETACH_TEAM_RESPONSIBILITY,
} from './actionTypes';
import {
  fetchTeamsSuccess, fetchTeamsFail,
  fetchTeamSuccess, fetchTeamFail,
  createTeamSuccess, createTeamFail,
  updateTeamSuccess, updateTeamFail,
  deleteTeamSuccess, deleteTeamFail,
  addTeamMemberSuccess, addTeamMemberFail,
  removeTeamMemberSuccess, removeTeamMemberFail,
  updateTeamMemberRoleSuccess, updateTeamMemberRoleFail,
  fetchTeamResponsibilitiesSuccess, fetchTeamResponsibilitiesFail,
  attachTeamResponsibilitySuccess, attachTeamResponsibilityFail,
  attachTeamResponsibilitiesBulkSuccess, attachTeamResponsibilitiesBulkFail,
  detachTeamResponsibilitySuccess, detachTeamResponsibilityFail,
} from './actions';

const errMsg = (e, fallback) => e?.response?.data?.message || fallback;

function* fetchTeamsSaga() {
  try {
    const { data } = yield call(api.get, 'team/all');
    yield put(fetchTeamsSuccess(data.response));
  } catch (error) {
    yield put(fetchTeamsFail(errMsg(error, 'Failed to fetch teams')));
  }
}

function* fetchTeamSaga({ payload: id }) {
  try {
    const { data } = yield call(api.get, `team?id=${id}`);
    yield put(fetchTeamSuccess(data.response));
  } catch (error) {
    yield put(fetchTeamFail(errMsg(error, 'Failed to fetch team')));
  }
}

function* createTeamSaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.post, 'team/add', payload);
    yield put(createTeamSuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to create team');
    yield put(createTeamFail(message));
    if (callback) callback(message);
  }
}

function* updateTeamSaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.put, 'team/update', payload);
    yield put(updateTeamSuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to update team');
    yield put(updateTeamFail(message));
    if (callback) callback(message);
  }
}

function* deleteTeamSaga({ payload: { id, callback } }) {
  try {
    yield call(api.delete, `team/delete?id=${id}`);
    yield put(deleteTeamSuccess(id));
    if (callback) callback(null, id);
  } catch (error) {
    const message = errMsg(error, 'Failed to delete team');
    yield put(deleteTeamFail(message));
    if (callback) callback(message);
  }
}

function* addTeamMemberSaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.post, 'team/member/add', payload);
    yield put(addTeamMemberSuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to add member');
    yield put(addTeamMemberFail(message));
    if (callback) callback(message);
  }
}

function* removeTeamMemberSaga({ payload: { teamId, userId, callback } }) {
  try {
    yield call(api.delete, `team/member/delete?teamId=${teamId}&userId=${userId}`);
    yield put(removeTeamMemberSuccess(userId));
    if (callback) callback(null, userId);
  } catch (error) {
    const message = errMsg(error, 'Failed to remove member');
    yield put(removeTeamMemberFail(message));
    if (callback) callback(message);
  }
}

function* updateTeamMemberRoleSaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.put, 'team/member/role', payload);
    yield put(updateTeamMemberRoleSuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to update member role');
    yield put(updateTeamMemberRoleFail(message));
    if (callback) callback(message);
  }
}

export default function* teamSaga() {
  yield takeLatest(FETCH_TEAMS, fetchTeamsSaga);
  yield takeLatest(FETCH_TEAM, fetchTeamSaga);
  yield takeLatest(CREATE_TEAM, createTeamSaga);
  yield takeLatest(UPDATE_TEAM, updateTeamSaga);
  yield takeLatest(DELETE_TEAM, deleteTeamSaga);
  yield takeLatest(ADD_TEAM_MEMBER, addTeamMemberSaga);
  yield takeLatest(REMOVE_TEAM_MEMBER, removeTeamMemberSaga);
  yield takeLatest(UPDATE_TEAM_MEMBER_ROLE, updateTeamMemberRoleSaga);
  yield takeLatest(FETCH_TEAM_RESPONSIBILITIES, fetchResponsibilitiesSaga);
  yield takeLatest(ATTACH_TEAM_RESPONSIBILITY, attachResponsibilitySaga);
  yield takeLatest(ATTACH_TEAM_RESPONSIBILITIES_BULK, attachResponsibilitiesBulkSaga);
  yield takeLatest(DETACH_TEAM_RESPONSIBILITY, detachResponsibilitySaga);
}

function* fetchResponsibilitiesSaga({ payload: teamId }) {
  try {
    const { data } = yield call(api.get, `team/responsibility/by-team?teamId=${teamId}`);
    yield put(fetchTeamResponsibilitiesSuccess(data.response));
  } catch (error) {
    yield put(fetchTeamResponsibilitiesFail(errMsg(error, 'Failed to fetch responsibilities')));
  }
}

function* attachResponsibilitySaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.post, 'team/responsibility/attach', payload);
    yield put(attachTeamResponsibilitySuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to attach resource');
    yield put(attachTeamResponsibilityFail(message));
    if (callback) callback(message);
  }
}

function* attachResponsibilitiesBulkSaga({ payload: { data: payload, callback } }) {
  try {
    const { data } = yield call(api.post, 'team/responsibility/attach-bulk', payload);
    yield put(attachTeamResponsibilitiesBulkSuccess(data.response));
    if (callback) callback(null, data.response);
  } catch (error) {
    const message = errMsg(error, 'Failed to attach resources');
    yield put(attachTeamResponsibilitiesBulkFail(message));
    if (callback) callback(message);
  }
}

function* detachResponsibilitySaga({ payload: { data: payload, callback } }) {
  try {
    const params = new URLSearchParams({
      teamId: payload.teamId,
      targetType: payload.targetType,
      targetId: payload.targetId,
    });
    yield call(api.delete, `team/responsibility/detach?${params.toString()}`);
    yield put(detachTeamResponsibilitySuccess(payload));
    if (callback) callback(null, payload);
  } catch (error) {
    const message = errMsg(error, 'Failed to detach resource');
    yield put(detachTeamResponsibilityFail(message));
    if (callback) callback(message);
  }
}
